import React from "react";
import { Link } from "react-router-dom";
import { Target, ArrowUpRight, Snowflake, Zap, PartyPopper } from "lucide-react";
import { fmt, fmtDate } from "@/lib/constants";
import { PROGRESS_BAR } from "@/lib/intent";

export default function DebtProgressCard({ progress }) {
  const { totals, plan, target } = progress;
  if (!totals.debts_total) return null;
  const pct = totals.pct_paid || 0;
  const StratIcon = plan.strategy === "snowball" ? Snowflake : Zap;

  return (
    <div className="p-6 rounded-2xl border border-border bg-card" data-testid="dash-debt-progress">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold">Debt payoff progress</h3>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <StratIcon className="h-3 w-3" /> {plan.strategy === "snowball" ? "Snowball" : "Avalanche"} plan · {totals.debts_paid_off}/{totals.debts_total} cleared
            {totals.projected_payoff_date && <> · debt-free {fmtDate(totals.projected_payoff_date)}</>}
          </div>
        </div>
        <div className="text-3xl font-display font-extrabold tabular text-emerald-500" data-testid="dash-debt-pct">{pct}%</div>
      </div>
      <div className="h-3 bg-accent rounded-full overflow-hidden">
        <div className={`h-full ${PROGRESS_BAR} transition-[width] duration-700`} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
      <div className="flex justify-between text-[11px] text-muted-foreground mt-1.5">
        <span>{fmt(totals.principal_paid)} paid down</span>
        <span>{fmt(totals.current_balance)} left of {fmt(totals.starting_balance)}</span>
      </div>

      {target ? (
        <div className="mt-4 p-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 flex items-center justify-between gap-3 flex-wrap" data-testid="dash-snowball-target">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center shrink-0"><Target className="h-4 w-4" /></div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-400">This month's snowball target</div>
              <div className="font-semibold truncate">{target.name} <span className="text-xs text-muted-foreground font-normal">· due {fmtDate(target.due_date)} · {fmt(target.balance)} left</span></div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono font-bold text-xl" data-testid="dash-target-amount">{fmt(target.recommended)}</div>
            <div className="text-[10px] text-muted-foreground">{target.extra > 0 ? `incl. ${fmt(target.extra)} snowball` : "minimum only"} · {fmt(totals.monthly_commitment)} total this month</div>
          </div>
        </div>
      ) : (
        <div className="mt-4 text-sm text-emerald-500 font-semibold flex items-center gap-1.5"><PartyPopper className="h-4 w-4" /> Every debt is paid off.</div>
      )}
      <Link to="/debts" className="mt-3 inline-flex items-center gap-1 text-xs text-indigo-400 hover:underline" data-testid="dash-debts-link">Open tracker <ArrowUpRight className="h-3 w-3" /></Link>
    </div>
  );
}
